import Hotjar from "@hotjar/browser";
import _ from "lodash";

import { UserWithCompany } from "frontend/utils/types";
import { isCompanyAdmin, isComprehensiveInternalUser, isSuperUser } from "lib/shared/permissions";

type HotjarUserInfo = Parameters<typeof Hotjar.identify>[1];
type HotjarSiteId = Parameters<typeof Hotjar.init>[0];

function getHotjarSiteId() {
  const siteId = process.env.NEXT_PUBLIC_HOTJAR_ID as unknown as HotjarSiteId | undefined;
  return siteId;
}

function isHotjarEnabledForEnv(siteId: HotjarSiteId | undefined): siteId is HotjarSiteId {
  return !_.isNil(siteId);
}

export function initHotjar() {
  const siteId = getHotjarSiteId();
  if (!isHotjarEnabledForEnv(siteId)) return;
  const hotjarVersion = 6;
  Hotjar.init(siteId, hotjarVersion);
}

export function getHotjarSessionUserAttrs(
  user: UserWithCompany,
  impersonatorUser: UserWithCompany | null,
): HotjarUserInfo {
  const base = {
    id: user.id,
    name: user.name || "NO_USER_NAME",
    companyId: user.companyId || "NO_COMPANY_ID",
    companyName: user.company?.name || "NO_COMPANY_NAME",
    primaryEmail: user.emails?.find((e) => e.isPrimary)?.email || "NO_PRIMARY_EMAIL",
    isSuperUser: isSuperUser(user),
    isCompanyAdmin: isCompanyAdmin(user),
    isComprehensiveInternalUser: isComprehensiveInternalUser(user),
    isImpersonator: Boolean(impersonatorUser),
  };
  const impersonatorAttrs = impersonatorUser
    ? {
        impersonatorCompanyName: impersonatorUser.company?.name || "NO_IMPERSONATOR_COMPANY_NAME",
        impersonatorPrimaryEmail:
          impersonatorUser.emails?.find((e) => e.isPrimary)?.email ||
          "NO_IMPERSONATOR_PRIMARY_EMAIL",
        impersonatorName: impersonatorUser.name || "NO_IMPERSONATOR_NAME",
        impersonatorIsComprehensiveInternalUser: isComprehensiveInternalUser(impersonatorUser),
      }
    : {};

  // @ts-expect-error TS2322
  return { ...base, ...impersonatorAttrs };
}

export function identifyHotjarUser(
  user: UserWithCompany,
  impersonatorUser: UserWithCompany | null,
) {
  const siteId = getHotjarSiteId();
  if (!isHotjarEnabledForEnv(siteId)) return;
  if (!Hotjar.isReady()) return;
  const userAttrs = getHotjarSessionUserAttrs(user, impersonatorUser);
  Hotjar.identify(user.id, userAttrs);
}
